import React, { useState } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Stack,
  Chip,
  Divider,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TablePagination,
  Tooltip,
} from "@mui/material";
import swal from "sweetalert";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { FilterBar, ReportHeader, currencyFmt } from "./ReportsPages";
import { getMonthlyGroupPerformance } from "../../services/reportService";
import { useTranslation } from "react-i18next";

export function MonthlyGroupPerformanceReport() {
  const { t } = useTranslation();
  // State management
  const [filters, setFilters] = useState({ from: "", to: "", group: "" });
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(0); // TablePagination is 0-indexed
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleGenerateReport = async (currentPage = 1) => {
    if (!filters.group || !filters.from || !filters.to) {
      swal({
        title: t("Missing Filters"),
        text: t("Please select a group and a date range to generate the report."),
        icon: "warning",
      });
      return;
    }

    setLoading(true);
    setError(null);
    // Don't clear reportData on page change, only on new report generation
    if (currentPage === 1) {
      setReportData(null);
      setPage(0); // Reset to first page
    }

    try {
      const data = await getMonthlyGroupPerformance({
        groupId: filters.group,
        startDate: filters.from,
        endDate: filters.to,
        page: currentPage,
        pageSize: rowsPerPage,
      });
      setReportData(data);
    } catch (err) {
      setError(err.message);
      swal({ title: t("Error"), text: err.message, icon: "error" });
    } finally {
      setLoading(false);
    }
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
    handleGenerateReport(newPage + 1); // API is 1-indexed
  };

  const handleRowsPerPageChange = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); // Reset to first page
  };

  const handleExport = async (format) => {
    if (!reportData) {
      swal({ title: t("No Data"), text: t("Please generate a report first before exporting."), icon: "info" });
      return;
    }
    try {
      await getMonthlyGroupPerformance({ ...filters, groupId: filters.group, startDate: filters.from, endDate: filters.to, format });
    } catch (err) {
      swal({ title: t("Export Failed"), text: err.message, icon: "error" });
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 } }}>
      <ReportHeader
        title={t("Monthly Group Performance")}
        subtitle={t("Summary of contributions, loans, penalties and balances for the selected period.")}
        roleHint={t("Access: Chairperson / Secretary / Accountant / Admin / Desk Officer")}
      />

      <FilterBar
        filters={filters}
        onFilterChange={handleFilterChange}
        onGenerate={() => handleGenerateReport(1)}
        onExport={handleExport}
        isGenerating={loading}
      />

      {/* KPI Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary" fontWeight={700}>
                {t("Total Contributions")}
              </Typography>
              <Typography variant="h6" fontWeight={900}>
                {currencyFmt(reportData?.total_contributions)} TZS
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary" fontWeight={700}>
                {t("Total Income (Period)")}
              </Typography>
              <Typography variant="h6" fontWeight={900}>
                {currencyFmt(reportData?.total_income)} TZS
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary" fontWeight={700}>
                {t("Loan Disbursements")}
              </Typography>
              <Typography variant="h6" fontWeight={900}>
                {currencyFmt(reportData?.loans_disbursed)} TZS
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
            <CardContent>
              <Typography variant="caption" color="text.secondary" fontWeight={700}>
                {t("Group Balance")}
              </Typography>
              <Typography variant="h6" fontWeight={900}>
                {currencyFmt(reportData?.group_balance)} TZS
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {loading && <Typography>{t("Loading report...")}</Typography>}
      {error && <Typography color="error">{t("Error")}: {error}</Typography>}

      {!reportData && !loading && (
        <Typography color="text.secondary" align="center" sx={{ my: 4 }}>
          {t("Please select filters and click \"Generate\" to view the report.")}
        </Typography>
      )}

      {/* Detail Table */}
      {reportData && (
        <Card sx={{ borderRadius: 3, boxShadow: 3 }}>
          <CardContent>
            <Stack
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              sx={{ mb: 1 }}
            >
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="subtitle1" fontWeight={800}>
                  {t("Member Level Performance")}
                </Typography>
                <Tooltip title={t("Per-member summary for this period.")}>
                  <InfoOutlinedIcon fontSize="small" sx={{ color: "text.secondary" }} />
                </Tooltip>
              </Stack>
              <Chip
                size="small"
                label={`${t("Active")}: ${reportData.active_members} • ${t("Inactive")}: ${reportData.inactive_members}`}
              />
            </Stack>
            <Divider sx={{ mb: 2 }} />

            <Paper variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>{t("Member")}</TableCell>
                    <TableCell align="right">{t("Contributions")}</TableCell>
                    <TableCell align="right">{t("Loans Disbursed")}</TableCell>
                    <TableCell align="right">{t("Repayments")}</TableCell>
                    <TableCell align="right">{t("Penalties")}</TableCell>
                    <TableCell>{t("Status")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportData?.member_performance?.items?.map((row) => (
                    <TableRow key={row.id} hover>
                      <TableCell>{row.name}</TableCell>
                      <TableCell align="right">
                        {currencyFmt(row.contributions)} TZS
                      </TableCell>
                      <TableCell align="right">
                        {currencyFmt(row.loans_disbursed)} TZS
                      </TableCell>
                      <TableCell align="right">
                        {currencyFmt(row.repayments)} TZS
                      </TableCell>
                      <TableCell align="right">
                        {currencyFmt(row.penalties)} TZS
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={row.status}
                          color={row.status === "Active" ? "success" : "default"}
                        />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Paper>
            <TablePagination
              component="div"
              count={reportData.member_performance.total_members || 0}
              page={page}
              onPageChange={handlePageChange}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleRowsPerPageChange}
              rowsPerPageOptions={[10, 25, 50]}
              sx={{
                ".MuiTablePagination-toolbar": { pl: { xs: 0 } }
              }}
            />
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
